import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import HomeIcon from '@mui/icons-material/Home';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import LayersIcon from '@mui/icons-material/Layers';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GradeIcon from '@mui/icons-material/Grade';
import PlaceIcon from '@mui/icons-material/Place';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import TablePagination from '@mui/material/TablePagination';

import {
  UNIT_STATUS,
  getCategoryStyle,
  STAT_CARD_STYLES,
  INVENTORY_COLORS,
  ACTIVE_STATUS_CHIP,
} from '../../../../utils/inventory_helper';
import { useInventory } from '../../../../contexts/InventoryContext';
import AddNewUnit from '../AddNewUnit';
import CSVImportUnits from '../../components/CSVImportUnits';

const CATEGORY_FILTERS = [
  { value: 'All', label: 'All Categories', icon: ViewModuleIcon },
  { value: 'Residential', label: 'Residential', icon: HomeIcon },
  { value: 'Commercial', label: 'Commercial', icon: BusinessCenterIcon },
  { value: 'Plot', label: 'Plot', icon: PlaceIcon },
  { value: 'Other', label: 'Other', icon: ViewModuleIcon },
];

const PRICE_RANGE_OPTIONS = ['All', '₹50 L - ₹1 Cr', '₹1 Cr - ₹2 Cr', '₹2 Cr - ₹5 Cr', '₹5 Cr+'];
const SIZE_OPTIONS = ['All', 'Under 1,000 sq ft', '1,000 - 2,000 sq ft', '2,000 - 3,000 sq ft', '3,000+ sq ft'];

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];

const ConfigurationList = ({ projectId, configs = [], project, navigate, onConfigsRefetch }) => {
  const { createUnit, inventoryOptions, fetchUnitUiSchema } = useInventory();
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [priceRangeFilter, setPriceRangeFilter] = useState('All');
  const [sizeFilter, setSizeFilter] = useState('All');
  const [sortBy, setSortBy] = useState('Best Match');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [addUnitDrawerOpen, setAddUnitDrawerOpen] = useState(false);
  const [unitDrawerUiSchema, setUnitDrawerUiSchema] = useState(null);
  const [selectedConfigForUnit, setSelectedConfigForUnit] = useState(null);

  useEffect(() => {
    if (!addUnitDrawerOpen || !fetchUnitUiSchema) {
      setUnitDrawerUiSchema(null);
      return;
    }
    const c = selectedConfigForUnit || {};
    const propertyTypeId =
      c.property_type_id ?? (typeof c.property_type === 'object' ? c.property_type?.id : c.property_type);
    const categoryId = c.category_id ?? (typeof c.category === 'object' ? c.category?.id : c.category);
    fetchUnitUiSchema({ property_type_id: propertyTypeId, category_id: categoryId })
      .then((data) => setUnitDrawerUiSchema(data?.unit_schema ?? null))
      .catch(() => setUnitDrawerUiSchema(null));
  }, [addUnitDrawerOpen, fetchUnitUiSchema, selectedConfigForUnit?.id]);
  const [importUnitsCSVOpen, setImportUnitsCSVOpen] = useState(false);
  const [selectedConfigForImport, setSelectedConfigForImport] = useState(null);

  const stats = useMemo(() => {
    const list = configs || [];
    const totalConfigs = list.length;
    let totalUnits = 0;
    let available = 0;
    let sold = 0;
    list.forEach((c) => {
      const t = c.total ?? 0;
      totalUnits += t;
      available += c.available ?? 0;
      const s = c.sold ?? Math.max(0, t - (c.available ?? 0) - (c.booked ?? 0) - (c.hold ?? 0));
      sold += s;
    });
    return { totalConfigs, totalUnits, available, sold };
  }, [configs]);

  const filtered = useMemo(() => {
    let list = [...(configs || [])];
    if (categoryFilter !== 'All') {
      list = list.filter((c) => (c.category || '').toLowerCase() === categoryFilter.toLowerCase());
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((c) => (c.name || '').toLowerCase().includes(q));
    }
    return list;
  }, [configs, categoryFilter, search]);

  const activeFiltersList = useMemo(() => {
    const list = [];
    if (categoryFilter !== 'All') list.push({ key: 'category', label: categoryFilter });
    if (priceRangeFilter !== 'All') list.push({ key: 'priceRange', label: priceRangeFilter });
    if (sizeFilter !== 'All') list.push({ key: 'size', label: sizeFilter });
    return list;
  }, [categoryFilter, priceRangeFilter, sizeFilter]);

  const clearAllFilters = () => {
    setCategoryFilter('All');
    setPriceRangeFilter('All');
    setSizeFilter('All');
    setSearch('');
    setPage(0);
  };

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  useEffect(() => {
    if (page > 0 && page * rowsPerPage >= filtered.length) setPage(0);
  }, [filtered.length, page, rowsPerPage]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (configId) => {
    navigate(`/inventory/projects/${projectId}/configurations/${configId}`);
  };

  const handleEditConfig = (e, configId) => {
    e.stopPropagation();
    navigate(`/inventory/projects/${projectId}/configurations/${configId}`);
  };

  const handleAddUnit = (e, config) => {
    e.stopPropagation();
    setSelectedConfigForUnit(config || null);
    setAddUnitDrawerOpen(true);
  };

  const handleImportUnitsCSV = (e, config) => {
    e.stopPropagation();
    setSelectedConfigForImport(config || null);
    setImportUnitsCSVOpen(true);
  };

  const unitStatusBar = (c) => {
    const total = c.total || 1;
    const a = c.available ?? 0;
    const b = c.booked ?? 0;
    const h = c.hold ?? 0;
    const s = c.sold ?? Math.max(0, total - a - b - h);
    const ap = total ? Math.round((a / total) * 100) : 0;
    const bp = total ? Math.round((b / total) * 100) : 0;
    const hp = total ? Math.round((h / total) * 100) : 0;
    const sp = total ? Math.round((s / total) * 100) : 0;
    return { a, b, h, s, ap, bp, hp, sp, total };
  };

  return (
    <Box>
      {/* Summary stats */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
        {[
          { ...STAT_CARD_STYLES[0], value: stats.totalConfigs, icon: ViewModuleIcon },
          { ...STAT_CARD_STYLES[1], value: stats.totalUnits, icon: LayersIcon },
          { ...STAT_CARD_STYLES[2], value: stats.available, icon: TrendingUpIcon },
          { ...STAT_CARD_STYLES[3], value: stats.sold, icon: GradeIcon },
        ].map((item) => (
          <Box
            key={item.label}
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: item.bg,
              border: '1px solid',
              borderColor: item.color + '30',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{item.label}</Typography>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>{item.value}</Typography>
            </Box>
            <Box sx={{ width: 40, height: 40, borderRadius: 1.25, bgcolor: item.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', color: item.color }}>
              <item.icon sx={{ fontSize: 22 }} />
            </Box>
          </Box>
        ))}
      </Box>

      {/* Search and filters card — one row: search | category | price | size */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            mb: 2,
          }}
        >
          <TextField
            size="small"
            placeholder="Search configurations by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 220, flex: '1 1 220px' }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={priceRangeFilter}
              onChange={(e) => setPriceRangeFilter(e.target.value)}
              displayEmpty
              startAdornment={
                <InputAdornment position="start" sx={{ mr: 0, '& .MuiSvgIcon-root': { color: 'text.secondary', fontSize: 20 } }}>
                  <TrendingUpIcon />
                </InputAdornment>
              }
              sx={{ bgcolor: 'grey.50', borderRadius: 1.25 }}
            >
              {PRICE_RANGE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              value={sizeFilter}
              onChange={(e) => setSizeFilter(e.target.value)}
              displayEmpty
              startAdornment={
                <InputAdornment position="start" sx={{ mr: 0, '& .MuiSvgIcon-root': { color: 'text.secondary', fontSize: 20 } }}>
                  <ViewModuleIcon />
                </InputAdornment>
              }
              sx={{ bgcolor: 'grey.50', borderRadius: 1.25 }}
            >
              {SIZE_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
          Category
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {CATEGORY_FILTERS.map((cat) => {
            const Icon = cat.icon;
            const selected = categoryFilter === cat.value;
            return (
              <Chip
                key={cat.value}
                icon={<Icon sx={{ fontSize: 18 }} />}
                label={cat.label}
                onClick={() => setCategoryFilter(cat.value)}
                variant={selected ? 'filled' : 'outlined'}
                size="small"
                sx={{
                  ...(selected
                    ? { bgcolor: "primary.main", color: 'white', '& .MuiChip-icon': { color: 'white' }, '&:hover': { bgcolor:   "primary.dark" } }
                    : { borderColor: "primary.main", '& .MuiChip-icon': { color: 'text.secondary' } }),
                }}
              />
            );
          })}
        </Box>

        {(activeFiltersList.length > 0 || search.trim()) && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <FilterListIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>Active Filters:</Typography>
              {search.trim() && (
                <Chip
                  size="small"
                  label={`Search: "${search}"`}
                  onDelete={() => setSearch('')}
                  deleteIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                  sx={{ bgcolor: INVENTORY_COLORS.greenLight, color: INVENTORY_COLORS.green, fontWeight: 600 }}
                />
              )}
              {activeFiltersList.map((f) => (
                <Chip
                  key={f.key}
                  size="small"
                  label={f.label}
                  onDelete={() => {
                    if (f.key === 'category') setCategoryFilter('All');
                    if (f.key === 'priceRange') setPriceRangeFilter('All');
                    if (f.key === 'size') setSizeFilter('All');
                  }}
                  deleteIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                  sx={{ bgcolor: INVENTORY_COLORS.greenLight, color: INVENTORY_COLORS.green, fontWeight: 600 }}
                />
              ))}
            </Box>
            <Typography
              component="button"
              type="button"
              onClick={clearAllFilters}
              sx={{ fontSize: '0.8125rem', fontWeight: 600, color: INVENTORY_COLORS.clearAllRed, cursor: 'pointer', background: 'none', border: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              Clear All
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Table header: Showing X of Y + Filtered chip + Export */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filtered.length} of {configs?.length ?? 0} configurations
          </Typography>
          {(categoryFilter !== 'All' || search.trim() || priceRangeFilter !== 'All' || sizeFilter !== 'All') && (
            <Chip label="Filtered" size="small" sx={{ bgcolor: INVENTORY_COLORS.filterChipBg, color: INVENTORY_COLORS.filterChipBlue, fontWeight: 600 }} />
          )}
        </Box>
        <Button variant="outlined" size="small" startIcon={<DownloadIcon />} sx={{ textTransform: 'none' }}>Export</Button>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Configuration</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Units Status</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Area Range</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <CurrencyRupeeIcon sx={{ fontSize: 14 }} />
                  Price Range (₹)
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.05em' }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.map((c) => {
              const us = unitStatusBar(c);
              const isCommercial = (c.category || '').toLowerCase() === 'commercial';
              const hasCommission = c.commission != null && c.commission !== '';
              const commType = c.commissionType === 'Custom' ? 'Custom' : 'Standard';
              const categoryStyle = getCategoryStyle(c.category || 'Other');
              return (
                <TableRow
                  key={c.id}
                  hover
                  onClick={() => handleRowClick(c.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                      <Box sx={{ mt: 0.25, color: 'text.secondary' }}>
                        {isCommercial ? <BusinessCenterIcon sx={{ fontSize: 20 }} /> : <HomeIcon sx={{ fontSize: 20 }} />}
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{c.name}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.25 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              px: 0.75,
                              py: 0.25,
                              borderRadius: 1,
                              bgcolor: categoryStyle.bg,
                              color: categoryStyle.color,
                              border: '1px solid',
                              borderColor: categoryStyle.border,
                              fontWeight: 600,
                            }}
                          >
                            {c.category || '—'}
                          </Typography>
                          {hasCommission && (
                            <Typography variant="caption" color="text.secondary">
                              • {c.commission}% {commType}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ minWidth: 200 }}>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                        <Typography variant="body2" fontWeight={600}>{us.total} Units</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', height: 6, borderRadius: 1, overflow: 'hidden', bgcolor: 'grey.200', mb: 0.75 }}>
                        {us.ap > 0 && <Box sx={{ width: `${us.ap}%`, bgcolor: UNIT_STATUS.available.color }} />}
                        {us.bp > 0 && <Box sx={{ width: `${us.bp}%`, bgcolor: UNIT_STATUS.booked.color }} />}
                        {us.hp > 0 && <Box sx={{ width: `${us.hp}%`, bgcolor: UNIT_STATUS.hold.color }} />}
                        {us.sp > 0 && <Box sx={{ width: `${us.sp}%`, bgcolor: UNIT_STATUS.sold.color }} />}
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                        {us.a > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: UNIT_STATUS.available.color }} />
                            <Typography variant="caption" color="text.secondary">{us.a} Avail</Typography>
                          </Box>
                        )}
                        {us.b > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: UNIT_STATUS.booked.color }} />
                            <Typography variant="caption" color="text.secondary">{us.b} Book</Typography>
                          </Box>
                        )}
                        {us.h > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: UNIT_STATUS.hold.color }} />
                            <Typography variant="caption" color="text.secondary">{us.h} Hold</Typography>
                          </Box>
                        )}
                        {us.s > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: UNIT_STATUS.sold.color }} />
                            <Typography variant="caption" color="text.secondary">{us.s} Sold</Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{c.areaRange ?? '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{c.priceRange ?? '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label="Active"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        bgcolor: ACTIVE_STATUS_CHIP.bg,
                        color: ACTIVE_STATUS_CHIP.color,
                        border: `1px solid ${ACTIVE_STATUS_CHIP.border}`,
                        '& .MuiChip-icon': { color: ACTIVE_STATUS_CHIP.color },
                      }}
                      icon={<Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: ACTIVE_STATUS_CHIP.dotColor, ml: 0.5 }} component="span" />}
                    />
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                        onClick={(e) => handleAddUnit(e, c)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          minWidth: 'auto',
                          px: 1.25,
                          bgcolor: 'success.main',
                          '&:hover': { bgcolor: 'success.dark' },
                        }}
                      >
                        Add Unit
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
      </TableContainer>

      {filtered.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          No configurations match your filters.
        </Typography>
      )}

      <AddNewUnit
        open={addUnitDrawerOpen}
        onClose={() => {
          setAddUnitDrawerOpen(false);
          setSelectedConfigForUnit(null);
        }}
        config={selectedConfigForUnit || {}}
        project={project || {}}
        projectId={projectId}
        configurationId={selectedConfigForUnit?.id}
        propertyTypes={inventoryOptions?.property_types ?? []}
        uiSchema={unitDrawerUiSchema}
        unitStatusOptions={inventoryOptions?.unit_statuses ?? []}
        facingOptions={inventoryOptions?.facing_directions ?? []}
        areaUnitOptions={inventoryOptions?.area_units ?? []}
        onSubmit={async (apiPayload) => {
          const configId = selectedConfigForUnit?.id;
          if (!projectId || !configId) return;
          await createUnit(projectId, configId, apiPayload);
          if (onConfigsRefetch) await onConfigsRefetch();
          setAddUnitDrawerOpen(false);
          setSelectedConfigForUnit(null);
        }}
      />

      <CSVImportUnits
        open={importUnitsCSVOpen}
        onClose={() => {
          setImportUnitsCSVOpen(false);
          setSelectedConfigForImport(null);
        }}
        configurationName={selectedConfigForImport?.name}
        areaUnit={selectedConfigForImport?.area_unit || 'sqft'}
        onImport={async (apiPayloads) => {
          const configId = selectedConfigForImport?.id;
          if (!projectId || !configId) return;
          for (const payload of apiPayloads) {
            await createUnit(projectId, configId, payload);
          }
          if (onConfigsRefetch) await onConfigsRefetch();
          setImportUnitsCSVOpen(false);
          setSelectedConfigForImport(null);
        }}
      />
    </Box>
  );
};

export default ConfigurationList;
